import React, { useState, useEffect } from 'react';
import { FormField } from '../ui/FormField';
import { validators } from '../../helpers/validators';
import { useFormValidation } from '../../hooks/useFormValidation';
import { FormErrorSummary } from '../ui/FormErrorSummary';
import { StatusMessage } from '../ui/StatusMessage';
import { lookupPostcodeAddresses, AddressLookupResult } from '../../services/address/address.service';

export interface CustomerFormData {
    customerName: string;
    address1: string;
    address2?: string;
    townCity: string;
    county?: string;
    country: string;
    postCode: string;
}

interface CustomerFormProps {
    onSubmit: (data: CustomerFormData) => void;
    onCancel?: () => void;
    initialData?: Partial<CustomerFormData>;
    isLoading?: boolean;
    id?: string;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
    onSubmit,
    onCancel,
    initialData = {},
    isLoading = false,
    id
}) => {
    const { values, errors, handleChangeFormField, isValid = false, resetErrors, validateAll } =
        useFormValidation({
            initialValues: [{
                customerName: initialData.customerName || "",
                postCode: initialData.postCode || "",
                address1: initialData.address1 || "",
                address2: initialData.address2 || "",
                townCity: initialData.townCity || "",
                county: initialData.county || "",
                country: initialData.country || "",
            }],
            rules: {
                customerName: [
                    validators.required("Customer Name is required"),
                    validators.minLength(2, "Customer Name minimum length is 2 characters"),
                    validators.maxLength(50, "Customer Name maximum length is 50 characters"),
                ],
                postCode: [
                    validators.required("Post Code is required"),
                    validators.minLength(2, "Post Code minimum length is 2 characters"),
                    validators.maxLength(10, "Post Code maximum length is 10 characters"),
                    validators.pattern(/^[A-Za-z0-9 ]+$/, "Post Code can only contain letters, numbers and spaces")
                ],
                address1: [
                    validators.required("Address is required"),
                    validators.minLength(2, "Address minimum length is 2 characters"),
                    validators.maxLength(50, "Address maximum length is 50 characters"),
                ],
                address2: [],
                townCity: [
                    validators.required("Town/City is required"),
                    validators.minLength(2, "Town/City minimum length is 2 characters"),
                    validators.maxLength(50, "Town/City maximum length is 50 characters"),
                ],
                county: [
                    validators.required("County is required"),
                    validators.minLength(2, "County minimum length is 2 characters"),
                    validators.maxLength(50, "County maximum length is 50 characters"),
                ],
                country: [
                    validators.required("Country is required"),
                    validators.minLength(2, "Country minimum length is 2 characters"),
                    validators.maxLength(50, "Country maximum length is 50 characters"),
                ],
            },
        });

    const [formData, setFormData] = useState<CustomerFormData>({
        customerName: initialData.customerName || '',
        address1: initialData.address1 || '',
        address2: initialData.address2 || '',
        townCity: initialData.townCity || '',
        county: initialData.county || '',
        country: initialData.country || '',
        postCode: initialData.postCode || ''
    });

    const isEditMode = Boolean(initialData.customerName);
    const [postcodeLookupLoading, setPostcodeLookupLoading] = useState<boolean>(false);
    const [postcodeLookupError, setPostcodeLookupError] = useState<string | null>(null);
    const [suggestedAddresses, setSuggestedAddresses] = useState<AddressLookupResult[]>([]);
    const [showFullForm, setShowFullForm] = useState<boolean>(isEditMode);
    const [showErrorSummary, setShowErrorSummary] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            return updated;
        });
        
        if (name === 'customerName' && value && value.trim().length > 0) {
            setShowFullForm(true);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const valid = validateAll(formData as any);

        let hasErrors = false;
        if (Array.isArray(valid)) {
            hasErrors = valid.some(v => v && Object.keys(v).length > 0);
        } else if (valid && typeof valid === 'object') {
            hasErrors = Object.keys(valid).length > 0;
        }

        if (hasErrors) {
            setShowErrorSummary(true);
            return;
        }
        onSubmit(formData);
    };

    const handleCloseStatusMessage = () => {
        setShowErrorSummary(false);
    };

    // Debounced postcode lookup
    useEffect(() => {
        let isMounted = true;
        let t: number | undefined;
        const pc = formData.postCode;
        
        if (pc && pc.trim().length >= 2) {
            setPostcodeLookupError(null);
            t = window.setTimeout(async () => {
                try {
                    setPostcodeLookupLoading(true);
                    const res = await lookupPostcodeAddresses(pc.trim());
                    if (!isMounted) return;
                    setSuggestedAddresses(res || []);
                } catch (err: any) {
                    if (!isMounted) return;
                    setPostcodeLookupError(err?.message || 'Lookup failed');
                    setSuggestedAddresses([]);
                } finally {
                    if (isMounted) setPostcodeLookupLoading(false);
                }
            }, 600);
        } else {
            setPostcodeLookupError(null);
            setPostcodeLookupLoading(false);
            setSuggestedAddresses([]);
        }
        return () => {
            isMounted = false;
            if (t) clearTimeout(t);
        };
    }, [formData.postCode]);

    return (
        <div id={id} className="customer-form">
            <form onSubmit={handleSubmit} className="form">
                <h3 className="form__title form__title--building">
                    {isEditMode ? 'Edit Customer' : 'Add Customer'}
                </h3>

                {showErrorSummary && [
                    ...new Set(
                        errors.flatMap(err => Object.values(err).filter(Boolean))
                    )
                ].length > 0 && (
                    <StatusMessage kind="error" onClose={handleCloseStatusMessage}>
                        <FormErrorSummary
                            errors={[
                                ...new Set(
                                    errors.flatMap(err => Object.values(err).filter(Boolean))
                                )
                            ]}
                        />
                    </StatusMessage>
                )}

                <div className="form__group">
                    <FormField
                        label="Customer Name"
                        name="customerName"
                        type="text"
                        value={formData.customerName}
                        showErrorMessage={false}
                        disabled={isLoading}
                        onChange={e => {
                            handleChangeFormField(0, "customerName")(e);
                            handleChange(e);
                        }}
                        error={errors[0]?.customerName}
                    />
                </div>

                <div className="form__group">
                    <FormField
                        label="Post Code"
                        name="postCode"
                        type="text"
                        value={formData.postCode}
                        showErrorMessage={false}
                        disabled={isLoading}
                        onChange={e => {
                            handleChangeFormField(0, "postCode")(e);
                            handleChange(e);
                        }}
                        error={errors[0]?.postCode}
                    />

                    <div className="mt-6">
                        {postcodeLookupLoading && <small className="text-muted">Checking postcode…</small>}
                        {postcodeLookupError && <small className="text-danger">{postcodeLookupError}</small>}
                    </div>

                    {suggestedAddresses?.length > 0 && (
                        <div className="form__group mt-1">
                            <label htmlFor="addressSelector" className="form__label">Select address</label>
                            <select
                                id="addressSelector"
                                className="form__control"
                                onChange={e => {
                                    const idx = Number.parseInt(e.target.value, 10);
                                    if (Number.isNaN(idx)) return;

                                    const sel = suggestedAddresses[idx];
                                    if (!sel) return;

                                    setFormData(prev => ({
                                        ...prev,
                                        address1: sel.address1 || prev.address1,
                                        townCity: sel.townCity || prev.townCity,
                                        county: sel.county || prev.county,
                                        country: sel.country || prev.country,
                                        postCode: sel.postCode || prev.postCode
                                    }));

                                    if (sel.address1) {
                                        handleChangeFormField(0, "address1")({ target: { value: sel.address1 } } as any);
                                    }
                                    if (sel.townCity) {
                                        handleChangeFormField(0, "townCity")({ target: { value: sel.townCity } } as any);
                                    }
                                    if (sel.county) {
                                        handleChangeFormField(0, "county")({ target: { value: sel.county } } as any);
                                    }
                                    if (sel.country) {
                                        handleChangeFormField(0, "country")({ target: { value: sel.country } } as any);
                                    }
                                    if (sel.postCode) {
                                        handleChangeFormField(0, "postCode")({ target: { value: sel.postCode } } as any);
                                    }
                                    
                                    setShowFullForm(true);
                                    setSuggestedAddresses([]);
                                }}>
                                <option value="">-- select suggested address --</option>
                                {suggestedAddresses.map((s, i) => (
                                    <option key={i} value={String(i)}>
                                        {s.display ||
                                            [s.address1, s.townCity, s.county, s.postCode, s.country]
                                                .filter(Boolean)
                                                .join(', ')
                                        }
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {showFullForm && (
                    <div className="form__group">
                        <FormField
                            label="Address Line 1"
                            name="address1"
                            type="text"
                            value={formData.address1}
                            disabled={isLoading}
                            showErrorMessage={false}
                            onChange={e => {
                                handleChangeFormField(0, "address1")(e);
                                handleChange(e);
                            }}
                            error={errors[0]?.address1}
                        />
                    </div>
                )}

                <div className="form__group">
                    <FormField
                        label="Address Line 2"
                        name="address2"
                        type="text"
                        value={formData.address2}
                        disabled={isLoading}
                        onChange={e => {
                            handleChangeFormField(0, "address2")(e);
                            handleChange(e);
                        }}
                        error={errors[0]?.address2}
                    />
                </div>

                <div className="form__row">
                    <div className="form__group">
                        <FormField
                            label="Town/City"
                            name="townCity"
                            type="text"
                            value={formData.townCity}
                            disabled={isLoading}
                            showErrorMessage={false}
                            onChange={e => {
                                handleChangeFormField(0, "townCity")(e);
                                handleChange(e);
                            }}
                            error={errors[0]?.townCity}
                        />
                    </div>

                    <div className="form__group">
                        <FormField
                            label="County"
                            name="county"
                            type="text"
                            value={formData.county}
                            disabled={isLoading}
                            showErrorMessage={false}
                            onChange={e => {
                                handleChangeFormField(0, "county")(e);
                                handleChange(e);
                            }}
                            error={errors[0]?.county}
                        />
                    </div>
                </div>

                {showFullForm && (
                    <div className="form__row">
                        <div className="form__group">
                            <FormField
                                label="Country"
                                name="country"
                                type="text"
                                value={formData.country}
                                disabled={isLoading}
                                showErrorMessage={false}
                                onChange={e => {
                                    handleChangeFormField(0, "country")(e);
                                    handleChange(e);
                                }}
                                error={errors[0]?.country}
                            />
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default CustomerForm;
